import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionsContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, Users, ChevronDown, Zap } from "lucide-react";
import DepartmentFilter, { useDepartmentFilter } from "@/components/DepartmentFilter";
import FirmFilter from "@/components/FirmFilter";
import EmployeeStatusFilter from "@/components/EmployeeStatusFilter";
import { fmtDate } from "@/lib/format";
import ModuleHint from "@/components/ModuleHint";

const SHIFT_TYPES = ["arbeit","frei","urlaub","krank","schulung","buero","homeoffice","zeitausgleich","sonderurlaub","feiertag","unbezahlt_frei","karenz","unentschuldigt"];
const SHIFT_COLORS = {
  arbeit: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  frei: "bg-zinc-700/30 text-zinc-300 border-zinc-600/40",
  urlaub: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  krank: "bg-red-500/20 text-red-300 border-red-500/30",
  krankenstand: "bg-red-500/20 text-red-300 border-red-500/30",
  schulung: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  buero: "bg-blue-700/20 text-blue-300 border-blue-700/40",
  homeoffice: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  zeitausgleich: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
  sonderurlaub: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  feiertag: "bg-pink-500/20 text-pink-300 border-pink-500/30",
  unbezahlt_frei: "bg-zinc-600/30 text-zinc-300 border-zinc-500/40",
  karenz: "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30",
  unentschuldigt: "bg-rose-600/30 text-rose-300 border-rose-600/40",
};

function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate()+n); return x; }
function startOfWeek(d) { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0,0,0,0); return x; }
function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0,0,0,0); return x; }
function endOfMonth(d) { const x = new Date(d); x.setMonth(x.getMonth()+1); x.setDate(0); x.setHours(0,0,0,0); return x; }
function iso(d) { return d.toISOString().slice(0,10); }
function getWeekNumber(d) {
  const date = new Date(d.getTime()); date.setHours(0,0,0,0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

export default function Dienstplan() {
  const { can } = usePermissions();
  const canEdit = can("operations", "edit");
  const canCreate = can("operations", "create");
  const [mode, setMode] = useState("week");  // day / week / month
  const [anchor, setAnchor] = useState(startOfWeek(new Date()));
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [absenceTemplates, setAbsenceTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [massAction, setMassAction] = useState(false);
  const [deptFilter, setDeptFilter] = useDepartmentFilter("dienstplan");
  const [empStatus, setEmpStatus] = useState("aktiv");
  const [quickShiftId, setQuickShiftId] = useState("");   // active quick-template id
  const [quickAbsenceId, setQuickAbsenceId] = useState("");
  const [quickMode, setQuickMode] = useState("");         // "shift" | "absence" | "" — what clicking a cell will do

  const days = useMemo(() => {
    if (mode === "day") return [new Date(anchor)];
    if (mode === "week") return Array.from({length: 7}, (_, i) => addDays(anchor, i));
    const start = startOfMonth(anchor); const end = endOfMonth(anchor);
    const n = Math.round((end - start) / 86400000) + 1;
    return Array.from({length: n}, (_, i) => addDays(start, i));
  }, [anchor, mode]);

  async function load() {
    const empParams = {};
    if (empStatus) empParams.status = empStatus;
    if (deptFilter) empParams.department_id = deptFilter;
    const { data: emps } = await api.get("/employees", { params: empParams });
    setEmployees(emps);
    const from = iso(days[0]); const to = iso(days[days.length - 1]);
    const shiftParams = { date_from: from, date_to: to };
    if (deptFilter) shiftParams.department_id = deptFilter;
    if (empStatus) shiftParams.employee_status = empStatus;
    const { data: s } = await api.get("/shifts", { params: shiftParams });
    setShifts(s);
    // Fetch actual time entries in range (for Tag-view black bars)
    const teParams = { date_from: from, date_to: to };
    if (deptFilter) teParams.department_id = deptFilter;
    try {
      const { data: te } = await api.get("/time-entries", { params: teParams });
      setTimeEntries(te);
    } catch { setTimeEntries([]); }
    const [d, st, at] = await Promise.all([
      api.get("/departments"),
      api.get("/shift-templates"),
      api.get("/absence-templates"),
    ]);
    setDepartments(d.data);
    setShiftTemplates(st.data);
    setAbsenceTemplates(at.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [anchor, mode, deptFilter, empStatus]);

  const map = useMemo(() => {
    const m = {}; shifts.forEach(s => { m[`${s.employee_id}|${s.date}`] = s; }); return m;
  }, [shifts]);

  async function applyShiftTemplate(empId, date, tpl) {
    if (!tpl) return;
    try {
      const payload = {
        date, employee_id: empId,
        shift_type: tpl.shift_type || "arbeit",
        workplace: tpl.workplace || "",
        notes: tpl.notes || "",
        template_id: tpl.id,
      };
      const existing = map[`${empId}|${date}`];
      if (existing) await api.delete(`/shifts/${existing.id}`);
      await api.post("/shifts", payload);
      toast.success(`„${tpl.name}" eingetragen`);
      await load();
    } catch { toast.error("Fehler"); }
  }

  async function applyAbsenceTemplate(empId, date, tpl) {
    if (!tpl) return;
    try {
      const existing = map[`${empId}|${date}`];
      if (existing) await api.delete(`/shifts/${existing.id}`);
      await api.post("/shifts", { date, employee_id: empId, shift_type: tpl.shift_type, workplace: "", notes: tpl.notes_template || "" });
      toast.success(`„${tpl.name}" eingetragen`);
      await load();
    } catch { toast.error("Fehler"); }
  }

  async function applyQuick(empId, date) {
    if (quickMode === "shift" && quickShiftId) {
      const tpl = shiftTemplates.find(t => t.id === quickShiftId);
      await applyShiftTemplate(empId, date, tpl);
      return;
    }
    if (quickMode === "absence" && quickAbsenceId) {
      const tpl = absenceTemplates.find(t => t.id === quickAbsenceId);
      await applyAbsenceTemplate(empId, date, tpl);
      return;
    }
    // Fallback: open template-picker modal
    const emp = employees.find(e => e.id === empId);
    if (emp) setEditing({ emp, date, shift: map[`${empId}|${date}`] });
  }

  async function deleteShift(sid) {
    if (!window.confirm("Schicht löschen?")) return;
    try { await api.delete(`/shifts/${sid}`); toast.success("Gelöscht"); await load(); }
    catch { toast.error("Fehler"); }
  }

  // ── Phase 5Z.34 — Drag & Drop ─────────────────────────────────────────
  // Modifier-Keys:
  //   normal drop  → move shift to target cell (overwrites existing with confirm)
  //   Shift+drop   → swap two shifts atomically (only works if both cells have a shift)
  async function handleShiftDrop(srcShiftId, targetEmpId, targetDateIso, isSwapModifier) {
    const src = shifts.find((s) => s.id === srcShiftId);
    if (!src) return;
    // Dropping on same cell = noop
    if (src.employee_id === targetEmpId && src.date === targetDateIso) return;
    const targetShift = map[`${targetEmpId}|${targetDateIso}`];
    try {
      if (isSwapModifier && targetShift) {
        await api.post(`/shifts/${srcShiftId}/swap`, { target_shift_id: targetShift.id });
        toast.success("Schichten getauscht");
      } else if (targetShift) {
        if (!window.confirm("Zielzelle enthält bereits eine Schicht. Überschreiben?")) return;
        await api.post(`/shifts/${srcShiftId}/move`, {
          employee_id: targetEmpId, date: targetDateIso, overwrite_target_id: targetShift.id,
        });
        toast.success("Schicht verschoben (Ziel überschrieben)");
      } else {
        await api.post(`/shifts/${srcShiftId}/move`, { employee_id: targetEmpId, date: targetDateIso });
        toast.success("Schicht verschoben");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verschieben fehlgeschlagen");
    }
  }

  function navPrev() {
    if (mode === "day") setAnchor(addDays(anchor, -1));
    else if (mode === "week") setAnchor(addDays(anchor, -7));
    else setAnchor(addDays(anchor, -31));
  }
  function navNext() {
    if (mode === "day") setAnchor(addDays(anchor, 1));
    else if (mode === "week") setAnchor(addDays(anchor, 7));
    else setAnchor(addDays(anchor, 31));
  }
  function navToday() {
    if (mode === "day") setAnchor(new Date(new Date().setHours(0,0,0,0)));
    else if (mode === "week") setAnchor(startOfWeek(new Date()));
    else setAnchor(startOfMonth(new Date()));
  }

  const rangeLabel = mode === "day"
    ? anchor.toLocaleDateString("de-AT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
    : mode === "week"
      ? `KW ${getWeekNumber(days[0])} · ${iso(days[0])} – ${iso(days[6])}`
      : anchor.toLocaleDateString("de-AT", { month: "long", year: "numeric" });

  return (
    <div className="p-6 md:p-8" data-testid="dienstplan-page">
      <ModuleHint moduleKey="dienstplan" />
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-zinc-100">Dienstpläne &amp; Abwesenheiten</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            {mode === "week" ? "Wochenansicht" : "Monatsansicht"} · Vorlage auswählen und in eine Zelle klicken — oder Zelle klicken für manuelles Bearbeiten
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FirmFilter compact />
          <DepartmentFilter value={deptFilter} onChange={setDeptFilter} module="dienstplan" testid="dienstplan-dept-filter"/>
          <EmployeeStatusFilter value={empStatus} onChange={setEmpStatus} testid="dienstplan-empstatus-filter"/>
          <div className="inline-flex rounded border border-zinc-800 overflow-hidden">
            <button onClick={()=>{ setMode("day"); setAnchor(new Date(new Date().setHours(0,0,0,0))); }} data-testid="mode-day"
              className={`px-3 py-2 text-xs font-medium ${mode==="day" ? "bg-blue-600 text-white" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}>Tag</button>
            <button onClick={()=>{ setMode("week"); setAnchor(startOfWeek(anchor)); }} data-testid="mode-week"
              className={`px-3 py-2 text-xs font-medium ${mode==="week" ? "bg-blue-600 text-white" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}>Woche</button>
            <button onClick={()=>{ setMode("month"); setAnchor(startOfMonth(anchor)); }} data-testid="mode-month"
              className={`px-3 py-2 text-xs font-medium ${mode==="month" ? "bg-blue-600 text-white" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}>Monat</button>
          </div>
          {(canEdit || canCreate) && (
            <button onClick={()=>setMassAction(true)} data-testid="mass-action-btn" className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-300 hover:bg-zinc-800"><Users className="w-3.5 h-3.5"/>Massenaktion</button>
          )}
          <button onClick={navPrev} data-testid="nav-prev" className="p-2 bg-zinc-900 border border-zinc-800 rounded hover:bg-zinc-800"><ChevronLeft className="w-4 h-4 text-zinc-300"/></button>
          <div className="text-xs text-zinc-400 font-mono px-3 py-2 bg-zinc-900 border border-zinc-800 rounded tabular-nums">{rangeLabel}</div>
          <button onClick={navNext} data-testid="nav-next" className="p-2 bg-zinc-900 border border-zinc-800 rounded hover:bg-zinc-800"><ChevronRight className="w-4 h-4 text-zinc-300"/></button>
          <button onClick={navToday} className="px-3 py-2 text-xs bg-zinc-900 border border-zinc-800 rounded hover:bg-zinc-800 text-zinc-300">Heute</button>
        </div>
      </div>

      {/* Quick-Tools Panel */}
      {(canCreate || canEdit) && (
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 rounded-md p-4 mb-5" data-testid="quick-tools-panel">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">
                <Zap className="w-3 h-3"/> Quick-Schichtplan
              </div>
              <select
                value={quickShiftId}
                onChange={(e) => { setQuickShiftId(e.target.value); setQuickMode(e.target.value ? "shift" : ""); if (e.target.value) setQuickAbsenceId(""); }}
                data-testid="quick-shift-select"
                className={`w-full px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 ${quickMode === "shift" ? "border-emerald-500/60 ring-1 ring-emerald-500/30" : "border-zinc-800"}`}
              >
                <option value="">— Vorlage wählen —</option>
                {shiftTemplates.map(t => {
                  const dept = departments.find(d => d.id === t.department_id);
                  return (
                    <option key={t.id} value={t.id}>
                      {t.name} · {dept?.name || "Abt.?"} · {t.start_time}–{t.end_time || "??:??"}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Quick-Abwesenheit</div>
              <select
                value={quickAbsenceId}
                onChange={(e) => { setQuickAbsenceId(e.target.value); setQuickMode(e.target.value ? "absence" : ""); if (e.target.value) setQuickShiftId(""); }}
                data-testid="quick-absence-select"
                className={`w-full px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 ${quickMode === "absence" ? "border-amber-500/60 ring-1 ring-amber-500/30" : "border-zinc-800"}`}
              >
                <option value="">— Vorlage wählen —</option>
                {absenceTemplates.map(t => (
                  <option key={t.id} value={t.id}>{t.name} ({t.shift_type})</option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[180px]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Aktion</div>
              <div className="flex gap-2 h-[38px]">
                {(quickMode === "shift" || quickMode === "absence") ? (
                  <button
                    onClick={() => { setQuickMode(""); setQuickShiftId(""); setQuickAbsenceId(""); }}
                    data-testid="quick-mode-clear"
                    className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-300 hover:bg-zinc-800"
                  >
                    Modus beenden
                  </button>
                ) : (
                  <div className="flex-1 px-3 py-2 bg-zinc-950 border border-zinc-800 border-dashed rounded text-xs text-zinc-500 italic">
                    Vorlage oben wählen
                  </div>
                )}
              </div>
            </div>
          </div>
          {quickMode && (
            <div className="mt-3 text-[11px] text-zinc-400 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/>
              {quickMode === "shift"
                ? <>Klicke auf eine leere Zelle, um <span className="text-emerald-400 font-semibold">„{shiftTemplates.find(t=>t.id===quickShiftId)?.name}"</span> einzutragen.</>
                : <>Klicke auf eine Zelle, um <span className="text-amber-400 font-semibold">„{absenceTemplates.find(t=>t.id===quickAbsenceId)?.name}"</span> einzutragen.</>}
            </div>
          )}
          {shiftTemplates.length === 0 && (
            <div className="mt-3 text-[11px] text-zinc-500 italic">
              Noch keine Schichtplan-Vorlagen angelegt. Gehe zu <a href="/einstellungen" className="text-blue-400 hover:underline">Einstellungen → Vorlagen → Schichtplan</a>, um welche anzulegen.
            </div>
          )}
        </div>
      )}

      {/* Grouped view per department (swim-lanes) */}
      {mode === "week" && (canEdit || canCreate) && (
        <div className="text-[10px] text-zinc-500 -mt-1 mb-2 flex items-center gap-1.5" data-testid="dienstplan-dnd-hint">
          <span className="inline-block w-1 h-1 rounded-full bg-emerald-500"></span>
          <span>Schichten per Drag &amp; Drop verschieben · <kbd className="px-1 py-0.5 text-[9px] bg-zinc-800 border border-zinc-700 rounded font-mono">Shift</kbd> + Drop = Tauschen</span>
        </div>
      )}
      {mode === "day" ? (
        <DayTimelineGrid
          day={days[0]}
          employees={employees}
          shifts={shifts}
          timeEntries={timeEntries}
          map={map}
          departments={departments}
          deptFilter={deptFilter}
          onEditShift={(emp, d, s) => (canEdit || canCreate) && setEditing({ emp, date: iso(d), shift: s, mode: "picker" })}
          onCreateOrQuick={(emp, d) => (canEdit || canCreate) && applyQuick(emp.id, iso(d))}
        />
      ) : mode === "week" ? (
        <GroupedWeekGrid
          days={days}
          employees={employees}
          shifts={shifts}
          map={map}
          departments={departments}
          deptFilter={deptFilter}
          onCellClick={(emp, d) => {
            const existing = map[`${emp.id}|${iso(d)}`];
            if (existing && !quickMode) {
              if (canEdit || canCreate) setEditing({ emp, date: iso(d), shift: existing, mode: "picker" });
            } else if (canEdit || canCreate) {
              applyQuick(emp.id, iso(d));
            }
          }}
          onShiftDelete={canEdit ? deleteShift : null}
          onShiftDrop={canEdit ? handleShiftDrop : null}
        />
      ) : (
        <MonthGrid days={days} employees={employees} map={map} anchor={anchor} onClick={(emp, d)=> (canEdit || canCreate) && setEditing({emp, date: iso(d), shift: map[`${emp.id}|${iso(d)}`], mode: "picker"})}/>
      )}

      {editing && (
        editing.mode === "detail" && editing.shift ? (
          <ShiftEditModal
            emp={editing.emp}
            date={editing.date}
            shift={editing.shift}
            shiftTemplates={shiftTemplates}
            onClose={() => setEditing(null)}
            onSaved={async () => { setEditing(null); await load(); }}
            canDelete={!!canEdit}
          />
        ) : (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="shift-set-modal">
            <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="p-5 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{editing.shift ? "Schicht ändern" : "Schicht setzen"}</div>
                  <div className="font-display font-semibold text-zinc-50 mt-1">{editing.emp.first_name} {editing.emp.last_name}</div>
                  <div className="text-xs text-zinc-400 font-mono">{fmtDate(editing.date)}</div>
                </div>
                <button onClick={()=>setEditing(null)} data-testid="shift-set-close" className="text-zinc-400 hover:text-zinc-200"><X className="w-5 h-5"/></button>
              </div>

              <div className="p-5 space-y-5 overflow-y-auto">
                {/* Current shift indicator (if any) */}
                {editing.shift && (
                  <div data-testid="shift-set-current" className={`px-3 py-2.5 border rounded flex items-center justify-between gap-2 ${SHIFT_COLORS[editing.shift.shift_type] || "bg-zinc-950 border-zinc-800 text-zinc-300"}`}>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider opacity-70">Aktuell eingetragen</div>
                      <div className="text-sm font-semibold uppercase tracking-wide">{editing.shift.shift_type}</div>
                      {(editing.shift.workplace || editing.shift.start_time) && (
                        <div className="text-[10px] opacity-80 font-mono mt-0.5">
                          {editing.shift.workplace || ""}
                          {editing.shift.start_time && editing.shift.end_time ? ` · ${editing.shift.start_time}–${editing.shift.end_time}` : ""}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {canEdit && (
                        <button
                          onClick={() => setEditing({ ...editing, mode: "detail" })}
                          data-testid="shift-set-edit-detail"
                          className="px-3 py-1.5 text-[11px] bg-zinc-900/60 hover:bg-zinc-900 border border-zinc-700 rounded text-zinc-200"
                        >
                          Detail bearbeiten
                        </button>
                      )}
                      {canEdit && (
                        <button
                          onClick={async () => { await deleteShift(editing.shift.id); setEditing(null); }}
                          data-testid="shift-set-delete"
                          className="px-3 py-1.5 text-[11px] bg-red-900/40 hover:bg-red-900/60 text-red-300 border border-red-800 rounded"
                        >
                          Löschen
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* SECTION: Working — pick a shift template */}
                <div data-testid="shift-set-work-section">
                  <div className="flex items-center justify-between mb-2">
                    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-emerald-400 font-semibold">
                      <Zap className="w-3 h-3"/> Arbeitet — Schichtplan-Vorlage wählen
                    </div>
                    {shiftTemplates.length > 0 && <div className="text-[10px] text-zinc-500">{shiftTemplates.length} Vorlagen</div>}
                  </div>
                  {shiftTemplates.length === 0 ? (
                    <div className="px-3 py-3 bg-zinc-950 border border-zinc-800 border-dashed rounded text-[11px] text-zinc-500 italic">
                      Noch keine Schichtplan-Vorlagen. Lege welche in <a href="/einstellungen" className="text-blue-400 hover:underline">Einstellungen → Vorlagen → Schichtplan</a> an.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {shiftTemplates.map(t => {
                        const dept = departments.find(d => d.id === t.department_id);
                        return (
                          <button key={t.id}
                            data-testid={`shift-set-tpl-${t.id}`}
                            onClick={() => { applyShiftTemplate(editing.emp.id, editing.date, t); setEditing(null); }}
                            className="text-left px-3 py-2.5 bg-zinc-950 border border-zinc-800 hover:border-emerald-500/60 hover:bg-emerald-500/5 rounded transition-all group"
                          >
                            <div className="flex items-center gap-2">
                              <span className="w-1 h-7 rounded flex-shrink-0" style={{ backgroundColor: t.color || "#3b82f6" }}/>
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-semibold text-zinc-100 truncate group-hover:text-emerald-300">{t.name}</div>
                                <div className="text-[10px] text-zinc-500 font-mono tabular-nums">
                                  {t.start_time}–{t.end_time || "??:??"} · {dept?.name || "Abt.?"}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* SECTION: Not working — pick an absence template */}
                <div data-testid="shift-set-absence-section">
                  <div className="flex items-center justify-between mb-2">
                    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-amber-400 font-semibold">
                      Arbeitet NICHT — Abwesenheits-Vorlage wählen
                    </div>
                    {absenceTemplates.length > 0 && <div className="text-[10px] text-zinc-500">{absenceTemplates.length} Vorlagen</div>}
                  </div>
                  {absenceTemplates.length === 0 ? (
                    <div className="px-3 py-3 bg-zinc-950 border border-zinc-800 border-dashed rounded text-[11px] text-zinc-500 italic">
                      Noch keine Abwesenheits-Vorlagen (Frei, Urlaub, Zeitausgleich, Krank …). Lege welche in <a href="/einstellungen" className="text-blue-400 hover:underline">Einstellungen → Vorlagen → Abwesenheit</a> an.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {absenceTemplates.map(t => (
                        <button key={t.id}
                          data-testid={`absence-set-tpl-${t.id}`}
                          onClick={() => { applyAbsenceTemplate(editing.emp.id, editing.date, t); setEditing(null); }}
                          className={`text-left px-3 py-2.5 border rounded transition-all hover:opacity-80 ${SHIFT_COLORS[t.shift_type] || "bg-zinc-950 border-zinc-800 text-zinc-300"}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.color || "#f59e0b" }}/>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-semibold truncate">{t.name}</div>
                              <div className="text-[9px] uppercase tracking-wider opacity-70">{t.shift_type}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer hint */}
                <div className="pt-1 text-[10px] text-zinc-600 italic text-center">
                  Tipp: Vorlagen sparen Zeit — definiere häufige Schichten &amp; Abwesenheitsarten einmal in den Einstellungen.
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {massAction && <MassActionDialog employees={employees} shiftTemplates={shiftTemplates} absenceTemplates={absenceTemplates} departments={departments} onClose={()=>setMassAction(false)} onDone={load}/>}
    </div>
  );
}

function WeekGrid({ days, employees, map, onClick }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-800">
            <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-semibold sticky left-0 bg-zinc-900 min-w-[180px]">Mitarbeiter</th>
            {days.map(d => (
              <th key={iso(d)} className="px-3 py-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold text-center min-w-[110px]">
                <div>{d.toLocaleDateString("de-AT", { weekday: "short" })}</div>
                <div className="font-mono text-zinc-400 tabular-nums mt-0.5">{d.getDate()}.{d.getMonth()+1}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {employees.map(e => (
            <tr key={e.id} className="border-b border-zinc-800 hover:bg-zinc-800/30">
              <td className="px-4 py-2 sticky left-0 bg-zinc-900 font-medium text-zinc-100 text-sm">
                {e.first_name} {e.last_name}
                <div className="text-[10px] text-zinc-500 capitalize mt-0.5">{e.role}</div>
              </td>
              {days.map(d => {
                const s = map[`${e.id}|${iso(d)}`];
                const cls = s ? SHIFT_COLORS[s.shift_type] : "bg-zinc-950 text-zinc-600 border-zinc-800 border-dashed";
                return (
                  <td key={iso(d)} className="px-2 py-1.5 text-center">
                    <button onClick={()=>onClick(e, d)} className={`w-full py-1.5 px-2 text-[10px] font-semibold uppercase tracking-wide rounded border transition-all ${cls} hover:opacity-80`}>{s ? s.shift_type : "+"}</button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// GroupedWeekGrid — swim-lanes grouped by department with counters
// ============================================================
function GroupedWeekGrid({ days, employees, shifts, map, departments, deptFilter, onCellClick, onShiftDelete, onShiftDrop }) {
  // Phase 5Z.34 — drag state held inside the grid so cells can highlight
  const [dragId, setDragId] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  function handleDragStart(e, shiftId) {
    setDragId(shiftId);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", shiftId); } catch { /* ignore */ }
  }
  function handleDragOver(e, cellKey) {
    if (!dragId || !onShiftDrop) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = e.shiftKey ? "link" : "move";
    if (cellKey !== dragOverKey) setDragOverKey(cellKey);
  }
  function handleDrop(e, empId, dIso) {
    if (!dragId || !onShiftDrop) return;
    e.preventDefault();
    const sid = dragId;
    const isSwap = e.shiftKey;
    setDragId(null);
    setDragOverKey(null);
    onShiftDrop(sid, empId, dIso, isSwap);
  }
  function handleDragEnd() { setDragId(null); setDragOverKey(null); }

  // Group employees by department_id (fall back to "")
  const groups = useMemo(() => {
    const m = {};
    employees.forEach(e => {
      const k = e.department_id || "";
      if (!m[k]) m[k] = [];
      m[k].push(e);
    });
    const order = deptFilter ? [deptFilter] : [...Object.keys(m)];
    if (!deptFilter && m[""] && !order.includes("")) order.push("");
    return order
      .filter(id => m[id])
      .map(id => ({
        id,
        department: departments.find(d => d.id === id),
        employees: m[id].sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)),
      }));
  }, [employees, departments, deptFilter]);

  const [collapsed, setCollapsed] = useState({});

  function stats(group) {
    const ids = new Set(group.employees.map(e => e.id));
    let planned = 0, absent = 0;
    shifts.forEach(s => {
      if (!ids.has(s.employee_id)) return;
      if (["arbeit", "buero", "homeoffice"].includes(s.shift_type)) planned += 1;
      if (["urlaub", "krank", "frei"].includes(s.shift_type)) absent += 1;
    });
    const active = group.employees.filter(e => e.status === "aktiv").length;
    return { active, planned, absent };
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto" data-testid="grouped-dienstplan-grid">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-800">
            <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-semibold sticky left-0 bg-zinc-900 min-w-[220px]">Mitarbeiter</th>
            {days.map(d => {
              const today = d.toISOString().slice(0, 10) === new Date().toISOString().slice(0, 10);
              return (
                <th key={iso(d)} className={`px-3 py-3 text-[10px] uppercase tracking-wider font-semibold text-center min-w-[110px] ${today ? "text-blue-400" : "text-zinc-500"}`}>
                  <div>{d.toLocaleDateString("de-AT", { weekday: "short" })}</div>
                  <div className="font-mono text-zinc-400 tabular-nums mt-0.5">{d.getDate()}.{d.getMonth()+1}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {groups.map(group => {
            const { active, planned, absent } = stats(group);
            const name = group.department?.name || (group.id ? "Unbekannt" : "Ohne Abteilung");
            const color = group.department?.color || "#6b7280";
            const isCollapsed = collapsed[group.id];
            return (
              <React.Fragment key={group.id || "none"}>
                <tr className="border-b border-zinc-800 cursor-pointer hover:brightness-110"
                  style={{ backgroundColor: `${color}20` }}
                  onClick={() => setCollapsed(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                  data-testid={`dept-group-${group.id || "none"}`}>
                  <td className="px-4 py-2.5 sticky left-0 font-semibold" style={{ backgroundColor: `${color}20`, color: "#fff" }}>
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-4 h-4 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} style={{ color }}/>
                      <span style={{ color }}>{name}</span>
                      <span className="text-[10px] text-zinc-300 font-mono">
                        Aktiv: <span className="font-bold">{active}</span>
                        {" · "}Geplant: <span className="font-bold">{planned}</span>
                        {" · "}Abw: <span className="font-bold">{absent}</span>
                      </span>
                    </div>
                  </td>
                  <td colSpan={days.length} className="px-3 py-2.5 text-right text-[10px] text-zinc-400" style={{ backgroundColor: `${color}20` }}>
                    {group.employees.length} Mitarbeiter · Klick zum {isCollapsed ? "Öffnen" : "Einklappen"}
                  </td>
                </tr>
                {!isCollapsed && group.employees.map(e => (
                  <tr key={e.id} className="border-b border-zinc-800 hover:bg-zinc-800/30">
                    <td className="px-4 py-2 sticky left-0 bg-zinc-900 font-medium text-zinc-100 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-8 rounded-sm" style={{ backgroundColor: color }}/>
                        <div>
                          {e.first_name} {e.last_name}
                          <div className="text-[10px] text-zinc-500 capitalize mt-0.5">{e.role}</div>
                        </div>
                      </div>
                    </td>
                    {days.map(d => {
                      const s = map[`${e.id}|${iso(d)}`];
                      const isToday = d.toISOString().slice(0, 10) === new Date().toISOString().slice(0, 10);
                      const cls = s ? SHIFT_COLORS[s.shift_type] : "bg-zinc-950 text-zinc-600 border-zinc-800 border-dashed";
                      const cellKey = `${e.id}|${iso(d)}`;
                      const isDragOver = dragOverKey === cellKey && dragId && (!s || s.id !== dragId);
                      const isDraggingThis = s && dragId === s.id;
                      return (
                        <td key={iso(d)}
                            className={`px-2 py-1.5 text-center relative ${isToday ? "bg-blue-950/20" : ""} ${isDragOver ? "bg-emerald-950/40" : ""}`}
                            onDragOver={onShiftDrop ? (ev) => handleDragOver(ev, cellKey) : undefined}
                            onDragLeave={onShiftDrop ? () => setDragOverKey(null) : undefined}
                            onDrop={onShiftDrop ? (ev) => handleDrop(ev, e.id, iso(d)) : undefined}>
                          <button onClick={() => onCellClick(e, d)}
                            data-testid={`cell-${e.id}-${iso(d)}`}
                            draggable={!!s && !!onShiftDrop}
                            onDragStart={(s && onShiftDrop) ? (ev) => handleDragStart(ev, s.id) : undefined}
                            onDragEnd={(s && onShiftDrop) ? handleDragEnd : undefined}
                            className={`w-full py-1.5 px-2 text-[10px] font-semibold uppercase tracking-wide rounded border transition-all ${cls} hover:opacity-80 group relative ${isDraggingThis ? "opacity-40 scale-95" : ""} ${isDragOver ? "ring-2 ring-emerald-500 ring-offset-1 ring-offset-zinc-900" : ""}`}>
                            {s ? (
                              <>
                                <div>{s.shift_type}</div>
                                {s.start_time && s.end_time && (
                                  <div className="text-[9px] font-mono font-normal opacity-80">{s.start_time}–{s.end_time}</div>
                                )}
                                {onShiftDelete && (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(ev) => { ev.stopPropagation(); onShiftDelete(s.id); }}
                                    onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.stopPropagation(); onShiftDelete(s.id); } }}
                                    data-testid={`shift-delete-${s.id}`}
                                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 inline-flex items-center justify-center cursor-pointer"
                                    title="Löschen">
                                    <X className="w-3 h-3 text-white"/>
                                  </span>
                                )}
                              </>
                            ) : "+"}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {groups.length === 0 && (
            <tr><td colSpan={days.length + 1} className="p-8 text-center text-zinc-500 text-sm">Keine Mitarbeiter in dieser Auswahl.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}



function MonthGrid({ days, employees, map, anchor, onClick }) {
  // render calendar grid: one row per week, cells show stacked employee shifts (small)
  const firstWeekday = (days[0].getDay() + 6) % 7;
  const cells = Array.from({length: firstWeekday}, () => null).concat(days);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i+7));

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden">
      <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold border-b border-zinc-800">
        {["Mo","Di","Mi","Do","Fr","Sa","So"].map(w => <div key={w} className="px-3 py-2 text-center">{w}</div>)}
      </div>
      <div>
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 border-b border-zinc-800 last:border-0">
            {week.map((d, i) => (
              <div key={i} className={`p-2 min-h-[140px] border-r border-zinc-800 last:border-0 ${!d ? "bg-zinc-950" : ""}`}>
                {d && (
                  <div>
                    <div className="text-xs font-mono text-zinc-400 mb-1">{d.getDate()}</div>
                    <div className="space-y-0.5">
                      {employees.slice(0, 6).map(e => {
                        const s = map[`${e.id}|${iso(d)}`];
                        if (!s) return null;
                        return (
                          <button key={e.id} onClick={()=>onClick(e, d)} className={`w-full truncate text-left px-1.5 py-0.5 text-[9px] rounded border ${SHIFT_COLORS[s.shift_type]} hover:opacity-80`}>
                            {e.last_name} · {s.shift_type}
                          </button>
                        );
                      })}
                      {employees.length > 6 && (
                        <div className="text-[9px] text-zinc-600 italic">+{employees.filter(e => map[`${e.id}|${iso(d)}`]).length - 6} mehr</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function MassActionDialog({ employees, shiftTemplates, absenceTemplates, departments, onClose, onDone }) {
  const [selEmps, setSelEmps] = useState([]);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().slice(0,10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0,10));
  const [deptFilter, setDeptFilter] = useState("");
  const [actionKind, setActionKind] = useState("shift_template"); // shift_template / absence_template / shift_type / delete
  const [shiftTemplateId, setShiftTemplateId] = useState("");
  const [absenceTemplateId, setAbsenceTemplateId] = useState("");
  const [shiftType, setShiftType] = useState("arbeit");
  const [busy, setBusy] = useState(false);

  const filteredEmps = useMemo(
    () => deptFilter ? employees.filter(e => e.department_id === deptFilter) : employees,
    [employees, deptFilter],
  );
  // If a shift template is picked, auto-filter to its dept
  const shiftTpl = shiftTemplates.find(t => t.id === shiftTemplateId);
  const absenceTpl = absenceTemplates.find(t => t.id === absenceTemplateId);

  function toggle(eid) { setSelEmps(s => s.includes(eid) ? s.filter(x => x !== eid) : [...s, eid]); }
  function selectAllVisible() { setSelEmps(filteredEmps.map(e => e.id)); }
  function clearAll() { setSelEmps([]); }

  // When picking a shift template, propose its department as active filter
  useEffect(() => {
    if (shiftTpl && shiftTpl.department_id && !deptFilter) {
      setDeptFilter(shiftTpl.department_id);
      setSelEmps([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftTemplateId]);

  async function run() {
    if (selEmps.length === 0) { toast.error("Keine Mitarbeiter ausgewählt"); return; }
    if (!dateFrom || !dateTo || dateFrom > dateTo) { toast.error("Ungültiger Zeitraum"); return; }

    // Validate action-kind-specific inputs
    if (actionKind === "shift_template" && !shiftTemplateId) {
      toast.error("Bitte eine Schichtplan-Vorlage wählen"); return;
    }
    if (actionKind === "absence_template" && !absenceTemplateId) {
      toast.error("Bitte eine Abwesenheits-Vorlage wählen"); return;
    }
    // Phase G-UX (27.05.2026) — Mass-Delete: confirm before destructive op
    if (actionKind === "delete") {
      const days = Math.max(1, Math.round((new Date(dateTo) - new Date(dateFrom)) / 86400000) + 1);
      const ok = window.confirm(
        `ALLE Schichten (inkl. Urlaub/Abwesenheit) von ${selEmps.length} Mitarbeiter` +
        `${selEmps.length === 1 ? "" : "n"} im Zeitraum ${dateFrom} – ${dateTo} (${days} Tag${days === 1 ? "" : "e"}) ` +
        `werden GELÖSCHT. Diese Aktion kann nicht rückgängig gemacht werden.\n\nFortfahren?`
      );
      if (!ok) return;
      setBusy(true);
      try {
        const { data } = await api.post("/shifts/bulk-delete", {
          date_from: dateFrom, date_to: dateTo, employee_ids: selEmps,
        });
        toast.success(`${data.deleted_count} Schichten gelöscht`);
        onDone(); onClose();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Fehler beim Löschen");
      } finally {
        setBusy(false);
      }
      return;
    }

    setBusy(true);
    try {
      const { data: existing } = await api.get("/shifts", { params: { date_from: dateFrom, date_to: dateTo } });
      const exmap = {}; existing.forEach(s => { exmap[`${s.employee_id}|${s.date}`] = s; });
      const start = new Date(dateFrom); const end = new Date(dateTo);
      const ops = [];

      function buildPayload(dateStr, empId) {
        if (actionKind === "shift_template" && shiftTpl) {
          const endTime = (() => {
            const [h, m] = (shiftTpl.start_time || "07:00").split(":").map(n => parseInt(n, 10));
            const total = h * 60 + m + (shiftTpl.duration_minutes || 0);
            return `${String(Math.floor((total % 1440) / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
          })();
          return {
            date: dateStr, employee_id: empId,
            shift_type: shiftTpl.shift_type || "arbeit",
            workplace: shiftTpl.workplace || "",
            notes: shiftTpl.notes || "",
            template_id: shiftTpl.id,
            start_time: shiftTpl.start_time || "",
            end_time: endTime,
            break_minutes: shiftTpl.break_minutes || 0,
            planned_minutes: shiftTpl.duration_minutes || 0,
          };
        }
        if (actionKind === "absence_template" && absenceTpl) {
          return {
            date: dateStr, employee_id: empId,
            shift_type: absenceTpl.shift_type || "urlaub",
            workplace: "", notes: absenceTpl.notes_template || "",
          };
        }
        return { date: dateStr, employee_id: empId, shift_type: shiftType, workplace: "", notes: "" };
      }

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const ds = d.toISOString().slice(0, 10);
        for (const eid of selEmps) {
          const ex = exmap[`${eid}|${ds}`];
          const payload = buildPayload(ds, eid);
          // To cleanly apply template fields (incl. start/end), replace existing shift
          if (ex) {
            ops.push(api.delete(`/shifts/${ex.id}`).then(() => api.post("/shifts", payload)));
          } else {
            ops.push(api.post("/shifts", payload));
          }
        }
      }
      await Promise.all(ops);
      toast.success(`${ops.length} Schichten gesetzt`);
      onDone(); onClose();
    } catch { toast.error("Fehler"); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="mass-action-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Massenaktion</div>
            <h3 className="font-display font-semibold text-zinc-50 mt-1">Mehrere Schichten gleichzeitig setzen</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400">✕</button>
        </div>
        <div className="p-5 space-y-4">
          {/* Date + Dept row */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Von</label>
              <input type="date" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)}
                data-testid="mass-date-from"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Bis</label>
              <input type="date" value={dateTo} onChange={(e)=>setDateTo(e.target.value)}
                data-testid="mass-date-to"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Abteilung filtern</label>
              <select value={deptFilter} onChange={(e)=>{ setDeptFilter(e.target.value); setSelEmps([]); }}
                data-testid="mass-dept-filter"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"
                style={deptFilter ? (() => {
                  const d = departments.find(x => x.id === deptFilter);
                  return d ? { borderColor: `${d.color}80`, backgroundColor: `${d.color}10` } : {};
                })() : {}}>
                <option value="">Alle Abteilungen</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </div>

          {/* Action kind tabs */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-1.5">Aktion</label>
            <div className="inline-flex rounded border border-zinc-800 overflow-hidden w-full">
              {[
                ["shift_template", "Schichtplan-Vorlage"],
                ["absence_template", "Abwesenheits-Vorlage"],
                ["shift_type", "Einfacher Typ"],
                ["delete", "Schichten löschen"],
              ].map(([k, l]) => (
                <button key={k} onClick={() => setActionKind(k)}
                  data-testid={`mass-kind-${k}`}
                  className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                    actionKind === k
                      ? (k === "delete" ? "bg-rose-600 text-white" : "bg-blue-600 text-white")
                      : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                  }`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Action-specific input */}
          {actionKind === "shift_template" && (
            <div data-testid="mass-shift-template-section">
              <label className="text-[10px] uppercase text-zinc-500 block mb-1">Schichtplan-Vorlage</label>
              <select value={shiftTemplateId} onChange={(e)=>setShiftTemplateId(e.target.value)}
                data-testid="mass-shift-template"
                className={`w-full mt-1 px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 ${!shiftTemplateId ? "border-amber-500/60 ring-1 ring-amber-500/30" : "border-zinc-800"}`}>
                <option value="">— Vorlage wählen —</option>
                {shiftTemplates
                  .filter(t => !deptFilter || t.department_id === deptFilter)
                  .map(t => {
                    const dept = departments.find(d => d.id === t.department_id);
                    return <option key={t.id} value={t.id}>{t.name} · {dept?.name || "?"} · {t.start_time}–{t.end_time || "?"}</option>;
                  })}
              </select>
              {shiftTpl && (
                <div className="mt-2 bg-zinc-950 border border-emerald-500/20 rounded p-2 text-[11px] text-zinc-300 flex items-center gap-2">
                  <span className="w-1 h-4 rounded-sm" style={{ backgroundColor: shiftTpl.color || "#3b82f6" }}/>
                  <span className="font-semibold text-emerald-400">{shiftTpl.name}</span>
                  <span className="text-zinc-500">{shiftTpl.workplace || "—"}</span>
                  <span className="ml-auto font-mono">{shiftTpl.start_time}–{shiftTpl.end_time || "?"} · {Math.floor(((shiftTpl.duration_minutes || 0) - (shiftTpl.break_minutes || 0)) / 60)}h Netto</span>
                </div>
              )}
              {shiftTemplates.length === 0 && (
                <div className="mt-2 text-[11px] text-zinc-500 italic">Noch keine Schichtplan-Vorlagen. Gehe zu Einstellungen → Vorlagen → Schichtplan.</div>
              )}
            </div>
          )}

          {actionKind === "absence_template" && (
            <div data-testid="mass-absence-template-section">
              <label className="text-[10px] uppercase text-zinc-500 block mb-1">Abwesenheits-Vorlage</label>
              <select value={absenceTemplateId} onChange={(e)=>setAbsenceTemplateId(e.target.value)}
                data-testid="mass-absence-template"
                className={`w-full mt-1 px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 ${!absenceTemplateId ? "border-amber-500/60 ring-1 ring-amber-500/30" : "border-zinc-800"}`}>
                <option value="">— Vorlage wählen —</option>
                {absenceTemplates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.shift_type})</option>)}
              </select>
              {absenceTemplates.length === 0 && (
                <div className="mt-2 text-[11px] text-zinc-500 italic">Noch keine Abwesenheits-Vorlagen angelegt.</div>
              )}
            </div>
          )}

          {actionKind === "shift_type" && (
            <div>
              <label className="text-[10px] uppercase text-zinc-500 block mb-1">Schichttyp</label>
              <select value={shiftType} onChange={(e)=>setShiftType(e.target.value)}
                data-testid="mass-shift-type"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                {SHIFT_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}

          {actionKind === "delete" && (
            <div data-testid="mass-delete-section"
                 className="bg-rose-500/10 border border-rose-500/30 rounded p-3 text-[12px] text-rose-200">
              <div className="flex items-start gap-2">
                <span className="text-rose-400 mt-0.5">⚠</span>
                <div>
                  <div className="font-semibold text-rose-300 mb-1">Schichten unwiderruflich löschen</div>
                  <div className="text-rose-200/80 leading-snug">
                    Es werden ALLE Schichten der unten gewählten Mitarbeiter im angegebenen Zeitraum gelöscht — inklusive Urlaub, Krankenstand und sonstige Abwesenheiten. Die Tage bleiben anschließend leer.
                    Eine Bestätigung wird vor dem Löschen abgefragt.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Employee list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase text-zinc-500">
                Mitarbeiter ({selEmps.length} von {filteredEmps.length} gewählt{deptFilter ? " in Abteilung" : ""})
              </label>
              <div className="space-x-2">
                <button onClick={selectAllVisible} data-testid="mass-select-all" className="text-xs text-blue-400 hover:text-blue-300">
                  {deptFilter ? "Alle sichtbaren" : "Alle"}
                </button>
                <button onClick={clearAll} className="text-xs text-zinc-400 hover:text-zinc-200">Keine</button>
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto bg-zinc-950 border border-zinc-800 rounded p-2 grid grid-cols-2 gap-1">
              {filteredEmps.length === 0 && (
                <div className="col-span-2 text-center text-[11px] text-zinc-500 italic py-4">
                  Keine Mitarbeiter in dieser Abteilung.
                </div>
              )}
              {filteredEmps.map(e => (
                <label key={e.id} data-testid={`mass-emp-${e.id}`}
                  className={`flex items-center gap-2 px-2 py-1 text-sm rounded cursor-pointer ${selEmps.includes(e.id) ? "bg-blue-600/20 text-blue-200" : "text-zinc-300 hover:bg-zinc-800"}`}>
                  <input type="checkbox" checked={selEmps.includes(e.id)} onChange={()=>toggle(e.id)} className="rounded"/>
                  <span className="truncate">{e.first_name} {e.last_name}</span>
                  {!deptFilter && e.department_id && (() => {
                    const d = departments.find(x => x.id === e.department_id);
                    return d ? <span className="ml-auto text-[9px] px-1.5 rounded text-white" style={{ backgroundColor: d.color }}>{d.name}</span> : null;
                  })()}
                </label>
              ))}
            </div>
          </div>

          {/* Summary */}
          {selEmps.length > 0 && dateFrom && dateTo && (
            <div className={`border rounded p-3 text-[11px] ${
              actionKind === "delete"
                ? "bg-rose-500/10 border-rose-500/30 text-rose-200"
                : "bg-zinc-950 border-zinc-800 text-zinc-300"
            }`}>
              <span className={actionKind === "delete" ? "text-rose-300/70" : "text-zinc-500"}>
                {actionKind === "delete" ? "Löschvorschau: " : "Vorschau: "}
              </span>
              <span className={`font-semibold ${actionKind === "delete" ? "text-rose-100" : "text-zinc-100"}`}>
                {selEmps.length} Mitarbeiter × {Math.max(1, Math.round((new Date(dateTo) - new Date(dateFrom)) / 86400000) + 1)} Tage
                {actionKind === "delete" ? " (alle Schichten in diesem Bereich)" : ` = ${selEmps.length * Math.max(1, Math.round((new Date(dateTo) - new Date(dateFrom)) / 86400000) + 1)} Schichten`}
              </span>
              {actionKind === "shift_template" && shiftTpl && <> · Mit Vorlage <span className="text-emerald-400">„{shiftTpl.name}"</span></>}
              {actionKind === "absence_template" && absenceTpl && <> · Als <span className="text-amber-400">„{absenceTpl.name}"</span></>}
              {actionKind === "shift_type" && <> · Typ <span className="text-blue-400">{shiftType}</span></>}
            </div>
          )}
        </div>
        <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
          <button onClick={run} disabled={busy} data-testid="mass-apply-btn"
            className={`px-4 py-2 text-sm disabled:opacity-50 text-white rounded font-medium ${
              actionKind === "delete"
                ? "bg-rose-600 hover:bg-rose-700"
                : "bg-blue-600 hover:bg-blue-700"
            }`}>
            {busy
              ? (actionKind === "delete" ? "Lösche..." : "Setzt...")
              : (actionKind === "delete" ? "Löschen" : "Anwenden")}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// DayTimelineGrid — horizontal Gantt per driver for a single day
// Green bar = planned shift · Black bar = actual time entry
// ============================================================
function DayTimelineGrid({ day, employees, shifts, timeEntries, map, departments, deptFilter, onEditShift, onCreateOrQuick }) {
  const iso = day.toISOString().slice(0, 10);
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Group employees by dept
  const groups = useMemo(() => {
    const m = {};
    employees.forEach(e => {
      const k = e.department_id || "";
      if (!m[k]) m[k] = [];
      m[k].push(e);
    });
    const order = deptFilter ? [deptFilter] : [...Object.keys(m)];
    if (!deptFilter && m[""] && !order.includes("")) order.push("");
    return order
      .filter(id => m[id])
      .map(id => ({
        id,
        department: departments.find(d => d.id === id),
        employees: m[id].sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)),
      }));
  }, [employees, departments, deptFilter]);

  function timeToPercent(t) {
    if (!t) return null;
    const [h, m] = t.split(":").map(n => parseInt(n, 10));
    return ((h * 60 + m) / (24 * 60)) * 100;
  }

  function findTimeEntry(empId) {
    return timeEntries.find(t => t.employee_id === empId && t.date === iso);
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto" data-testid="day-timeline-grid">
      {/* Legend */}
      <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center gap-4 text-[10px] uppercase tracking-wider text-zinc-500">
        <div className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-emerald-500"/>
          Geplante Schicht
        </div>
        <div className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-zinc-200"/>
          Tatsächlich gearbeitet
        </div>
        <div className="ml-auto text-zinc-400">{day.toLocaleDateString("de-AT", { weekday: "long", day: "2-digit", month: "long" })}</div>
      </div>

      {/* Hour scale */}
      <div className="border-b border-zinc-800 grid" style={{ gridTemplateColumns: "220px 1fr" }}>
        <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Mitarbeiter</div>
        <div className="relative h-8 border-l border-zinc-800">
          {hours.map(h => (
            <div key={h} className="absolute top-0 h-full text-[9px] font-mono text-zinc-500 border-l border-zinc-800/60 pl-1"
              style={{ left: `${(h / 24) * 100}%`, width: `${100 / 24}%` }}>
              {String(h).padStart(2, "0")}
            </div>
          ))}
        </div>
      </div>

      {groups.map(group => {
        const color = group.department?.color || "#6b7280";
        const name = group.department?.name || (group.id ? "Unbekannt" : "Ohne Abteilung");
        return (
          <React.Fragment key={group.id || "none"}>
            <div className="px-4 py-2 font-semibold text-[11px]" style={{ backgroundColor: `${color}20`, color }}>
              <span>{name}</span>
              <span className="text-zinc-400 ml-2 font-mono text-[10px]">({group.employees.length})</span>
            </div>
            {group.employees.map(e => {
              const s = map[`${e.id}|${iso}`];
              const te = findTimeEntry(e.id);
              const shiftStart = s?.start_time ? timeToPercent(s.start_time) : null;
              const shiftEnd = s?.end_time ? timeToPercent(s.end_time) : null;
              const teStart = te?.start_time ? timeToPercent(te.start_time) : null;
              const teEnd = te?.end_time ? timeToPercent(te.end_time) : null;
              return (
                <div key={e.id} className="grid border-b border-zinc-800 hover:bg-zinc-800/20" style={{ gridTemplateColumns: "220px 1fr" }}>
                  <div className="px-4 py-3 text-sm font-medium text-zinc-100 flex items-center gap-2">
                    <div className="w-1 h-8 rounded-sm" style={{ backgroundColor: color }}/>
                    <div>
                      {e.first_name} {e.last_name}
                      <div className="text-[10px] text-zinc-500 capitalize mt-0.5">{e.role}</div>
                    </div>
                  </div>
                  <div className="relative h-16 border-l border-zinc-800">
                    {/* Hour grid lines */}
                    {hours.map(h => (
                      <div key={h} className="absolute top-0 h-full border-l border-zinc-800/40"
                        style={{ left: `${(h / 24) * 100}%` }}/>
                    ))}
                    {/* Click-to-create area if no shift */}
                    {!s && (
                      <button onClick={() => onCreateOrQuick(e, day)} data-testid={`day-create-${e.id}`}
                        className="absolute inset-0 w-full h-full text-center text-[10px] text-zinc-700 hover:text-zinc-500 hover:bg-zinc-800/20">
                        + Schicht anlegen
                      </button>
                    )}
                    {/* Planned shift — green */}
                    {s && shiftStart !== null && shiftEnd !== null && shiftEnd > shiftStart && (
                      <button
                        onClick={() => onEditShift(e, day, s)}
                        data-testid={`day-shift-${s.id}`}
                        className="absolute top-2 h-6 bg-emerald-500 hover:bg-emerald-400 rounded text-[10px] font-semibold text-emerald-950 px-2 border-2 border-emerald-400 transition-colors truncate"
                        style={{ left: `${shiftStart}%`, width: `${shiftEnd - shiftStart}%` }}
                        title={`${s.workplace || ""} · ${s.start_time}–${s.end_time}`}
                      >
                        {s.workplace || s.shift_type.toUpperCase()} · {s.start_time}–{s.end_time}
                      </button>
                    )}
                    {/* No start/end but shift exists → show full-day badge */}
                    {s && (shiftStart === null || shiftEnd === null) && (
                      <button onClick={() => onEditShift(e, day, s)} data-testid={`day-shift-${s.id}`}
                        className={`absolute top-2 left-0 right-0 mx-[1%] h-6 rounded text-[10px] font-semibold px-2 border-2 uppercase tracking-wide ${SHIFT_COLORS[s.shift_type] || "bg-zinc-500 text-white"}`}>
                        {s.shift_type} {s.workplace ? `· ${s.workplace}` : ""}
                      </button>
                    )}
                    {/* Actual time entry — black */}
                    {te && teStart !== null && teEnd !== null && teEnd > teStart && (
                      <div
                        className="absolute bottom-2 h-6 bg-zinc-200 hover:bg-white rounded text-[10px] font-semibold text-zinc-950 px-2 border-2 border-zinc-100 transition-colors truncate"
                        style={{ left: `${teStart}%`, width: `${teEnd - teStart}%` }}
                        title={`Ist: ${te.start_time}–${te.end_time} · ${te.work_minutes || 0} Min`}
                        data-testid={`day-te-${te.id}`}
                      >
                        IST · {te.start_time}–{te.end_time}
                      </div>
                    )}
                    {/* Only actual entry but no planned shift → show in red-ish warning */}
                    {!s && te && teStart !== null && teEnd !== null && teEnd > teStart && (
                      <div className="absolute top-1 left-1 text-[9px] font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/30 px-1.5 rounded">
                        ungeplant!
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
      {groups.length === 0 && (
        <div className="p-8 text-center text-zinc-500 text-sm">Keine Mitarbeiter in dieser Auswahl.</div>
      )}
    </div>
  );
}

// ============================================================
// ShiftEditModal — full detail form to edit an existing shift
// ============================================================
function ShiftEditModal({ emp, date, shift, shiftTemplates, onClose, onSaved, canDelete }) {
  const [form, setForm] = useState({
    date: shift.date || date,
    start_time: shift.start_time || "",
    end_time: shift.end_time || "",
    workplace: shift.workplace || "",
    notes: shift.notes || "",
    shift_type: shift.shift_type || "arbeit",
    break_minutes: shift.break_minutes || 0,
  });
  const [saving, setSaving] = useState(false);

  function applyTemplate(tplId) {
    const t = shiftTemplates.find(x => x.id === tplId);
    if (!t) return;
    setForm({
      ...form,
      workplace: t.workplace || form.workplace,
      start_time: t.start_time || form.start_time,
      end_time: t.end_time || form.end_time,
      shift_type: t.shift_type || form.shift_type,
      break_minutes: t.break_minutes ?? form.break_minutes,
    });
    toast.info(`Vorlage „${t.name}" angewendet`);
  }

  async function save() {
    if (form.start_time && form.end_time && form.start_time >= form.end_time) {
      toast.error("Dienstende muss nach Dienstbeginn liegen");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/shifts/${shift.id}`, {
        ...shift,
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        workplace: form.workplace,
        notes: form.notes,
        shift_type: form.shift_type,
        break_minutes: +form.break_minutes || 0,
      });
      toast.success("Schicht gespeichert");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!window.confirm("Diese Schicht wirklich löschen?")) return;
    try {
      await api.delete(`/shifts/${shift.id}`);
      toast.success("Schicht gelöscht");
      onSaved?.();
    } catch { toast.error("Fehler beim Löschen"); }
  }

  const plannedMinutes = (() => {
    if (!form.start_time || !form.end_time) return 0;
    const [sh, sm] = form.start_time.split(":").map(n => parseInt(n, 10));
    const [eh, em] = form.end_time.split(":").map(n => parseInt(n, 10));
    return (eh * 60 + em) - (sh * 60 + sm);
  })();
  const netMinutes = Math.max(0, plannedMinutes - (+form.break_minutes || 0));

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="shift-edit-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-lg">
        <div className="p-5 border-b border-zinc-800 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Schicht bearbeiten</div>
            <div className="font-display font-semibold text-zinc-50 mt-1 text-lg">{emp.first_name} {emp.last_name}</div>
            <div className="text-[11px] text-zinc-500 font-mono mt-0.5">ID {shift.id?.slice(0,8)}</div>
          </div>
          <button onClick={onClose} data-testid="shift-edit-close" className="text-zinc-400 hover:text-zinc-200"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-5 space-y-4">
          {shiftTemplates.length > 0 && (
            <div>
              <label className="text-[10px] uppercase text-zinc-500 block mb-1">Vorlage anwenden (optional)</label>
              <select onChange={(e) => { if (e.target.value) applyTemplate(e.target.value); e.target.value = ""; }}
                data-testid="shift-edit-template"
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                <option value="">— Vorlage wählen, um Felder zu überschreiben —</option>
                {shiftTemplates.map(t => (
                  <option key={t.id} value={t.id}>{t.name} · {t.start_time}–{t.end_time || "?"}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-1">Einsatzort</label>
            <input value={form.workplace} onChange={(e)=>setForm({...form, workplace: e.target.value})}
              data-testid="shift-edit-workplace"
              placeholder="z.B. Amazon DVI3"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-zinc-500 block mb-1">Datum</label>
              <input type="date" value={form.date} onChange={(e)=>setForm({...form, date: e.target.value})}
                data-testid="shift-edit-date"
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500 block mb-1">Schicht-Typ</label>
              <select value={form.shift_type} onChange={(e)=>setForm({...form, shift_type: e.target.value})}
                data-testid="shift-edit-type"
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                {SHIFT_TYPES.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase text-zinc-500 block mb-1">Dienstbeginn</label>
              <input type="time" value={form.start_time} onChange={(e)=>setForm({...form, start_time: e.target.value})}
                data-testid="shift-edit-start"
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500 block mb-1">Dienstende</label>
              <input type="time" value={form.end_time} onChange={(e)=>setForm({...form, end_time: e.target.value})}
                data-testid="shift-edit-end"
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500 block mb-1">Pause (Min)</label>
              <input type="number" value={form.break_minutes} onChange={(e)=>setForm({...form, break_minutes: e.target.value})}
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
          </div>

          {plannedMinutes > 0 && (
            <div className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-xs text-zinc-400">
              <span className="text-zinc-500">Geplant: </span>
              <span className="font-mono font-semibold text-emerald-400">{Math.floor(plannedMinutes/60)}h{plannedMinutes%60 ? `${plannedMinutes%60}m` : ""}</span>
              <span className="text-zinc-500"> · Netto: </span>
              <span className="font-mono font-semibold text-emerald-400">{Math.floor(netMinutes/60)}h{netMinutes%60 ? `${netMinutes%60}m` : ""}</span>
              <span className="text-zinc-500"> (abzüglich {form.break_minutes || 0}m Pause)</span>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-1">Beschreibung / Notizen</label>
            <textarea rows={3} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})}
              data-testid="shift-edit-notes"
              placeholder="Optional"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
          </div>
        </div>

        <div className="p-5 border-t border-zinc-800 flex justify-between gap-2">
          {canDelete ? (
            <button onClick={remove} data-testid="shift-edit-delete" className="px-4 py-2 text-sm bg-red-900/40 hover:bg-red-900/60 text-red-300 border border-red-800 rounded">Löschen</button>
          ) : <div/>}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">Abbrechen</button>
            <button onClick={save} disabled={saving} data-testid="shift-edit-save"
              className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded font-medium disabled:bg-zinc-800">
              {saving ? "Speichert…" : "Speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

